"use client"

import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { createPortal } from "react-dom"
import { cn } from "@/lib/utils"

// Comprehensive list of countries sorted alphabetically
// Covers all regions with significant WhatsApp usage
const COUNTRIES = [
    // A
    { code: "AF", dial: "+93", name: "Afghanistan", flag: "🇦🇫" },
    { code: "AL", dial: "+355", name: "Albania", flag: "🇦🇱" },
    { code: "DZ", dial: "+213", name: "Algeria", flag: "🇩🇿" },
    { code: "AD", dial: "+376", name: "Andorra", flag: "🇦🇩" },
    { code: "AO", dial: "+244", name: "Angola", flag: "🇦🇴" },
    { code: "AG", dial: "+1268", name: "Antigua and Barbuda", flag: "🇦🇬" },
    { code: "AR", dial: "+54", name: "Argentina", flag: "🇦🇷" },
    { code: "AM", dial: "+374", name: "Armenia", flag: "🇦🇲" },
    { code: "AU", dial: "+61", name: "Australia", flag: "🇦🇺" },
    { code: "AT", dial: "+43", name: "Austria", flag: "🇦🇹" },
    { code: "AZ", dial: "+994", name: "Azerbaijan", flag: "🇦🇿" },
    // B
    { code: "BH", dial: "+973", name: "Bahrain", flag: "🇧🇭" },
    { code: "BD", dial: "+880", name: "Bangladesh", flag: "🇧🇩" },
    { code: "BB", dial: "+1246", name: "Barbados", flag: "🇧🇧" },
    { code: "BY", dial: "+375", name: "Belarus", flag: "🇧🇾" },
    { code: "BE", dial: "+32", name: "Belgium", flag: "🇧🇪" },
    { code: "BZ", dial: "+501", name: "Belize", flag: "🇧🇿" },
    { code: "BJ", dial: "+229", name: "Benin", flag: "🇧🇯" },
    { code: "BT", dial: "+975", name: "Bhutan", flag: "🇧🇹" },
    { code: "BO", dial: "+591", name: "Bolivia", flag: "🇧🇴" },
    { code: "BA", dial: "+387", name: "Bosnia and Herzegovina", flag: "🇧🇦" },
    { code: "BW", dial: "+267", name: "Botswana", flag: "🇧🇼" },
    { code: "BR", dial: "+55", name: "Brazil", flag: "🇧🇷" },
    { code: "BN", dial: "+673", name: "Brunei", flag: "🇧🇳" },
    { code: "BG", dial: "+359", name: "Bulgaria", flag: "🇧🇬" },
    { code: "BF", dial: "+226", name: "Burkina Faso", flag: "🇧🇫" },
    // C
    { code: "KH", dial: "+855", name: "Cambodia", flag: "🇰🇭" },
    { code: "CM", dial: "+237", name: "Cameroon", flag: "🇨🇲" },
    { code: "CA", dial: "+1", name: "Canada", flag: "🇨🇦" },
    { code: "CV", dial: "+238", name: "Cape Verde", flag: "🇨🇻" },
    { code: "CF", dial: "+236", name: "Central African Republic", flag: "🇨🇫" },
    { code: "TD", dial: "+235", name: "Chad", flag: "🇹🇩" },
    { code: "CL", dial: "+56", name: "Chile", flag: "🇨🇱" },
    { code: "CN", dial: "+86", name: "China", flag: "🇨🇳" },
    { code: "CO", dial: "+57", name: "Colombia", flag: "🇨🇴" },
    { code: "CG", dial: "+242", name: "Congo", flag: "🇨🇬" },
    { code: "CD", dial: "+243", name: "Congo (DRC)", flag: "🇨🇩" },
    { code: "CR", dial: "+506", name: "Costa Rica", flag: "🇨🇷" },
    { code: "HR", dial: "+385", name: "Croatia", flag: "🇭🇷" },
    { code: "CU", dial: "+53", name: "Cuba", flag: "🇨🇺" },
    { code: "CY", dial: "+357", name: "Cyprus", flag: "🇨🇾" },
    { code: "CZ", dial: "+420", name: "Czech Republic", flag: "🇨🇿" },
    // D
    { code: "DK", dial: "+45", name: "Denmark", flag: "🇩🇰" },
    { code: "DJ", dial: "+253", name: "Djibouti", flag: "🇩🇯" },
    { code: "DM", dial: "+1767", name: "Dominica", flag: "🇩🇲" },
    { code: "DO", dial: "+1809", name: "Dominican Republic", flag: "🇩🇴" },
    // E
    { code: "EC", dial: "+593", name: "Ecuador", flag: "🇪🇨" },
    { code: "EG", dial: "+20", name: "Egypt", flag: "🇪🇬" },
    { code: "SV", dial: "+503", name: "El Salvador", flag: "🇸🇻" },
    { code: "GQ", dial: "+240", name: "Equatorial Guinea", flag: "🇬🇶" },
    { code: "ER", dial: "+291", name: "Eritrea", flag: "🇪🇷" },
    { code: "EE", dial: "+372", name: "Estonia", flag: "🇪🇪" },
    { code: "SZ", dial: "+268", name: "Eswatini", flag: "🇸🇿" },
    { code: "ET", dial: "+251", name: "Ethiopia", flag: "🇪🇹" },
    // F
    { code: "FJ", dial: "+679", name: "Fiji", flag: "🇫🇯" },
    { code: "FI", dial: "+358", name: "Finland", flag: "🇫🇮" },
    { code: "FR", dial: "+33", name: "France", flag: "🇫🇷" },
    // G
    { code: "GA", dial: "+241", name: "Gabon", flag: "🇬🇦" },
    { code: "GM", dial: "+220", name: "Gambia", flag: "🇬🇲" },
    { code: "GE", dial: "+995", name: "Georgia", flag: "🇬🇪" },
    { code: "DE", dial: "+49", name: "Germany", flag: "🇩🇪" },
    { code: "GH", dial: "+233", name: "Ghana", flag: "🇬🇭" },
    { code: "GR", dial: "+30", name: "Greece", flag: "🇬🇷" },
    { code: "GD", dial: "+1473", name: "Grenada", flag: "🇬🇩" },
    { code: "GT", dial: "+502", name: "Guatemala", flag: "🇬🇹" },
    { code: "GN", dial: "+224", name: "Guinea", flag: "🇬🇳" },
    { code: "GW", dial: "+245", name: "Guinea-Bissau", flag: "🇬🇼" },
    { code: "GY", dial: "+592", name: "Guyana", flag: "🇬🇾" },
    // H
    { code: "HT", dial: "+509", name: "Haiti", flag: "🇭🇹" },
    { code: "HN", dial: "+504", name: "Honduras", flag: "🇭🇳" },
    { code: "HK", dial: "+852", name: "Hong Kong", flag: "🇭🇰" },
    { code: "HU", dial: "+36", name: "Hungary", flag: "🇭🇺" },
    // I
    { code: "IS", dial: "+354", name: "Iceland", flag: "🇮🇸" },
    { code: "IN", dial: "+91", name: "India", flag: "🇮🇳" },
    { code: "ID", dial: "+62", name: "Indonesia", flag: "🇮🇩" },
    { code: "IR", dial: "+98", name: "Iran", flag: "🇮🇷" },
    { code: "IQ", dial: "+964", name: "Iraq", flag: "🇮🇶" },
    { code: "IE", dial: "+353", name: "Ireland", flag: "🇮🇪" },
    { code: "IL", dial: "+972", name: "Israel", flag: "🇮🇱" },
    { code: "IT", dial: "+39", name: "Italy", flag: "🇮🇹" },
    { code: "CI", dial: "+225", name: "Ivory Coast", flag: "🇨🇮" },
    // J
    { code: "JM", dial: "+1876", name: "Jamaica", flag: "🇯🇲" },
    { code: "JP", dial: "+81", name: "Japan", flag: "🇯🇵" },
    { code: "JO", dial: "+962", name: "Jordan", flag: "🇯🇴" },
    // K
    { code: "KZ", dial: "+7", name: "Kazakhstan", flag: "🇰🇿" },
    { code: "KE", dial: "+254", name: "Kenya", flag: "🇰🇪" },
    { code: "KW", dial: "+965", name: "Kuwait", flag: "🇰🇼" },
    { code: "KG", dial: "+996", name: "Kyrgyzstan", flag: "🇰🇬" },
    // L
    { code: "LA", dial: "+856", name: "Laos", flag: "🇱🇦" },
    { code: "LV", dial: "+371", name: "Latvia", flag: "🇱🇻" },
    { code: "LB", dial: "+961", name: "Lebanon", flag: "🇱🇧" },
    { code: "LS", dial: "+266", name: "Lesotho", flag: "🇱🇸" },
    { code: "LR", dial: "+231", name: "Liberia", flag: "🇱🇷" },
    { code: "LY", dial: "+218", name: "Libya", flag: "🇱🇾" },
    { code: "LI", dial: "+423", name: "Liechtenstein", flag: "🇱🇮" },
    { code: "LT", dial: "+370", name: "Lithuania", flag: "🇱🇹" },
    { code: "LU", dial: "+352", name: "Luxembourg", flag: "🇱🇺" },
    // M
    { code: "MO", dial: "+853", name: "Macao", flag: "🇲🇴" },
    { code: "MK", dial: "+389", name: "North Macedonia", flag: "🇲🇰" },
    { code: "MG", dial: "+261", name: "Madagascar", flag: "🇲🇬" },
    { code: "MW", dial: "+265", name: "Malawi", flag: "🇲🇼" },
    { code: "MY", dial: "+60", name: "Malaysia", flag: "🇲🇾" },
    { code: "MV", dial: "+960", name: "Maldives", flag: "🇲🇻" },
    { code: "ML", dial: "+223", name: "Mali", flag: "🇲🇱" },
    { code: "MT", dial: "+356", name: "Malta", flag: "🇲🇹" },
    { code: "MR", dial: "+222", name: "Mauritania", flag: "🇲🇷" },
    { code: "MU", dial: "+230", name: "Mauritius", flag: "🇲🇺" },
    { code: "MX", dial: "+52", name: "Mexico", flag: "🇲🇽" },
    { code: "MD", dial: "+373", name: "Moldova", flag: "🇲🇩" },
    { code: "MC", dial: "+377", name: "Monaco", flag: "🇲🇨" },
    { code: "MN", dial: "+976", name: "Mongolia", flag: "🇲🇳" },
    { code: "ME", dial: "+382", name: "Montenegro", flag: "🇲🇪" },
    { code: "MA", dial: "+212", name: "Morocco", flag: "🇲🇦" },
    { code: "MZ", dial: "+258", name: "Mozambique", flag: "🇲🇿" },
    { code: "MM", dial: "+95", name: "Myanmar", flag: "🇲🇲" },
    // N
    { code: "NA", dial: "+264", name: "Namibia", flag: "🇳🇦" },
    { code: "NP", dial: "+977", name: "Nepal", flag: "🇳🇵" },
    { code: "NL", dial: "+31", name: "Netherlands", flag: "🇳🇱" },
    { code: "NZ", dial: "+64", name: "New Zealand", flag: "🇳🇿" },
    { code: "NI", dial: "+505", name: "Nicaragua", flag: "🇳🇮" },
    { code: "NE", dial: "+227", name: "Niger", flag: "🇳🇪" },
    { code: "NG", dial: "+234", name: "Nigeria", flag: "🇳🇬" },
    { code: "NO", dial: "+47", name: "Norway", flag: "🇳🇴" },
    // O
    { code: "OM", dial: "+968", name: "Oman", flag: "🇴🇲" },
    // P
    { code: "PK", dial: "+92", name: "Pakistan", flag: "🇵🇰" },
    { code: "PS", dial: "+970", name: "Palestine", flag: "🇵🇸" },
    { code: "PA", dial: "+507", name: "Panama", flag: "🇵🇦" },
    { code: "PG", dial: "+675", name: "Papua New Guinea", flag: "🇵🇬" },
    { code: "PY", dial: "+595", name: "Paraguay", flag: "🇵🇾" },
    { code: "PE", dial: "+51", name: "Peru", flag: "🇵🇪" },
    { code: "PH", dial: "+63", name: "Philippines", flag: "🇵🇭" },
    { code: "PL", dial: "+48", name: "Poland", flag: "🇵🇱" },
    { code: "PT", dial: "+351", name: "Portugal", flag: "🇵🇹" },
    { code: "PR", dial: "+1787", name: "Puerto Rico", flag: "🇵🇷" },
    // Q
    { code: "QA", dial: "+974", name: "Qatar", flag: "🇶🇦" },
    // R
    { code: "RO", dial: "+40", name: "Romania", flag: "🇷🇴" },
    { code: "RU", dial: "+7", name: "Russia", flag: "🇷🇺" },
    { code: "RW", dial: "+250", name: "Rwanda", flag: "🇷🇼" },
    // S
    { code: "SA", dial: "+966", name: "Saudi Arabia", flag: "🇸🇦" },
    { code: "SN", dial: "+221", name: "Senegal", flag: "🇸🇳" },
    { code: "RS", dial: "+381", name: "Serbia", flag: "🇷🇸" },
    { code: "SC", dial: "+248", name: "Seychelles", flag: "🇸🇨" },
    { code: "SL", dial: "+232", name: "Sierra Leone", flag: "🇸🇱" },
    { code: "SG", dial: "+65", name: "Singapore", flag: "🇸🇬" },
    { code: "SK", dial: "+421", name: "Slovakia", flag: "🇸🇰" },
    { code: "SI", dial: "+386", name: "Slovenia", flag: "🇸🇮" },
    { code: "SO", dial: "+252", name: "Somalia", flag: "🇸🇴" },
    { code: "ZA", dial: "+27", name: "South Africa", flag: "🇿🇦" },
    { code: "KR", dial: "+82", name: "South Korea", flag: "🇰🇷" },
    { code: "SS", dial: "+211", name: "South Sudan", flag: "🇸🇸" },
    { code: "ES", dial: "+34", name: "Spain", flag: "🇪🇸" },
    { code: "LK", dial: "+94", name: "Sri Lanka", flag: "🇱🇰" },
    { code: "SD", dial: "+249", name: "Sudan", flag: "🇸🇩" },
    { code: "SR", dial: "+597", name: "Suriname", flag: "🇸🇷" },
    { code: "SE", dial: "+46", name: "Sweden", flag: "🇸🇪" },
    { code: "CH", dial: "+41", name: "Switzerland", flag: "🇨🇭" },
    { code: "SY", dial: "+963", name: "Syria", flag: "🇸🇾" },
    // T
    { code: "TW", dial: "+886", name: "Taiwan", flag: "🇹🇼" },
    { code: "TJ", dial: "+992", name: "Tajikistan", flag: "🇹🇯" },
    { code: "TZ", dial: "+255", name: "Tanzania", flag: "🇹🇿" },
    { code: "TH", dial: "+66", name: "Thailand", flag: "🇹🇭" },
    { code: "TG", dial: "+228", name: "Togo", flag: "🇹🇬" },
    { code: "TT", dial: "+1868", name: "Trinidad and Tobago", flag: "🇹🇹" },
    { code: "TN", dial: "+216", name: "Tunisia", flag: "🇹🇳" },
    { code: "TR", dial: "+90", name: "Turkey", flag: "🇹🇷" },
    { code: "TM", dial: "+993", name: "Turkmenistan", flag: "🇹🇲" },
    // U
    { code: "UG", dial: "+256", name: "Uganda", flag: "🇺🇬" },
    { code: "UA", dial: "+380", name: "Ukraine", flag: "🇺🇦" },
    { code: "AE", dial: "+971", name: "United Arab Emirates", flag: "🇦🇪" },
    { code: "GB", dial: "+44", name: "United Kingdom", flag: "🇬🇧" },
    { code: "US", dial: "+1", name: "United States", flag: "🇺🇸" },
    { code: "UY", dial: "+598", name: "Uruguay", flag: "🇺🇾" },
    { code: "UZ", dial: "+998", name: "Uzbekistan", flag: "🇺🇿" },
    // V
    { code: "VE", dial: "+58", name: "Venezuela", flag: "🇻🇪" },
    { code: "VN", dial: "+84", name: "Vietnam", flag: "🇻🇳" },
    // Y
    { code: "YE", dial: "+967", name: "Yemen", flag: "🇾🇪" },
    // Z
    { code: "ZM", dial: "+260", name: "Zambia", flag: "🇿🇲" },
    { code: "ZW", dial: "+263", name: "Zimbabwe", flag: "🇿🇼" },
] as const

type Country = (typeof COUNTRIES)[number]

interface PhoneInputProps {
    value: string
    onChange: (fullNumber: string) => void
    onBlur?: () => void
    placeholder?: string
    disabled?: boolean
    error?: string
}

interface DropdownPosition {
    top: number
    left: number
    width: number
    openAbove: boolean
}

// Get default country (US) for initial state
const DEFAULT_COUNTRY = COUNTRIES.find((c) => c.code === "US") || COUNTRIES[0]

/**
 * International phone input with country selector
 * Matches Outfame dashboard input styles (outlined variant)
 * Uses portal for dropdown to escape overflow:hidden containers
 */
export function PhoneInput({
    value,
    onChange,
    onBlur,
    placeholder = "Phone number",
    disabled = false,
    error
}: PhoneInputProps) {
    const [isOpen, setIsOpen] = useState(false)
    const [selectedCountry, setSelectedCountry] = useState<Country>(DEFAULT_COUNTRY)
    const [localNumber, setLocalNumber] = useState("")
    const [searchQuery, setSearchQuery] = useState("")
    const [highlightedIndex, setHighlightedIndex] = useState(0)
    const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null)

    const triggerRef = useRef<HTMLButtonElement>(null)
    const dropdownRef = useRef<HTMLDivElement>(null)
    const searchInputRef = useRef<HTMLInputElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const listRef = useRef<HTMLDivElement>(null)

    // Filter countries by search query (name or dial code)
    const filteredCountries = useMemo(() => {
        if (!searchQuery.trim()) return COUNTRIES
        const query = searchQuery.toLowerCase().trim()
        return COUNTRIES.filter(
            (c) =>
                c.name.toLowerCase().includes(query) ||
                c.dial.includes(query) ||
                c.code.toLowerCase().includes(query)
        )
    }, [searchQuery])

    // Calculate dropdown position
    const updateDropdownPosition = useCallback(() => {
        if (!triggerRef.current) return

        const rect = triggerRef.current.getBoundingClientRect()
        const dropdownHeight = 320 // max-h-80 = 320px
        const viewportHeight = window.innerHeight
        const spaceBelow = viewportHeight - rect.bottom
        const spaceAbove = rect.top

        // Prefer below, but flip if not enough space
        const openAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow

        setDropdownPosition({
            top: openAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
            left: rect.left,
            width: Math.max(280, rect.width),
            openAbove
        })
    }, [])

    // Detect country from browser locale on mount
    useEffect(() => {
        const detectCountry = () => {
            const locale = navigator.language || "en-US"
            const countryCode = locale.split("-")[1]?.toUpperCase()

            if (countryCode) {
                const found = COUNTRIES.find((c) => c.code === countryCode)
                if (found) {
                    setSelectedCountry(found)
                }
            }
        }

        detectCountry()
    }, [])

    // Parse incoming value to extract country and number
    useEffect(() => {
        if (value && value.startsWith("+")) {
            // Find ALL countries whose dial code matches the value prefix
            // Sort by dial code length descending so longer codes (e.g. +1268) match before shorter (+1)
            const matches = COUNTRIES
                .filter((c) => value.startsWith(c.dial))
                .sort((a, b) => b.dial.length - a.dial.length)

            if (matches.length > 0) {
                // Use the longest (most specific) dial code match
                let best = matches[0]

                // When multiple countries share the exact same dial code (e.g. US/CA both +1),
                // disambiguate using browser locale, falling back to US
                const sameDial = matches.filter((m) => m.dial === best.dial)
                if (sameDial.length > 1) {
                    const localeCode = (typeof navigator !== "undefined" ? navigator.language : "en-US")
                        .split("-")[1]?.toUpperCase()
                    const localeMatch = sameDial.find((c) => c.code === localeCode)
                    const usMatch = sameDial.find((c) => c.code === "US")
                    best = localeMatch || usMatch || sameDial[0]
                }

                setSelectedCountry(best)
                setLocalNumber(value.slice(best.dial.length).replace(/\D/g, ""))
            }
        }
    }, [])

    // Update position on open, scroll, resize
    useEffect(() => {
        if (!isOpen) return

        updateDropdownPosition()

        const handleUpdate = () => updateDropdownPosition()
        window.addEventListener("scroll", handleUpdate, true)
        window.addEventListener("resize", handleUpdate)

        return () => {
            window.removeEventListener("scroll", handleUpdate, true)
            window.removeEventListener("resize", handleUpdate)
        }
    }, [isOpen, updateDropdownPosition])

    // Focus search input when dropdown opens
    useEffect(() => {
        if (isOpen) {
            // Small delay to ensure portal is mounted
            const timer = setTimeout(() => {
                searchInputRef.current?.focus()
            }, 10)
            return () => clearTimeout(timer)
        } else {
            // Reset search and highlighted index on close
            setSearchQuery("")
            setHighlightedIndex(0)
        }
    }, [isOpen])

    // Close dropdown on outside click
    useEffect(() => {
        if (!isOpen) return

        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node
            if (
                triggerRef.current &&
                !triggerRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                setIsOpen(false)
            }
        }

        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [isOpen])

    // Keyboard navigation
    useEffect(() => {
        if (!isOpen) return

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case "Escape":
                    e.preventDefault()
                    setIsOpen(false)
                    triggerRef.current?.focus()
                    break
                case "ArrowDown":
                    e.preventDefault()
                    setHighlightedIndex((prev) =>
                        prev < filteredCountries.length - 1 ? prev + 1 : 0
                    )
                    break
                case "ArrowUp":
                    e.preventDefault()
                    setHighlightedIndex((prev) =>
                        prev > 0 ? prev - 1 : filteredCountries.length - 1
                    )
                    break
                case "Enter":
                    e.preventDefault()
                    if (filteredCountries[highlightedIndex]) {
                        handleCountrySelect(filteredCountries[highlightedIndex])
                    }
                    break
                case "Tab":
                    setIsOpen(false)
                    break
            }
        }

        document.addEventListener("keydown", handleKeyDown)
        return () => document.removeEventListener("keydown", handleKeyDown)
    }, [isOpen, highlightedIndex, filteredCountries])

    // Scroll highlighted item into view
    useEffect(() => {
        if (!isOpen || !listRef.current) return

        const highlightedElement = listRef.current.querySelector(
            `[data-index="${highlightedIndex}"]`
        )
        if (highlightedElement) {
            highlightedElement.scrollIntoView({ block: "nearest" })
        }
    }, [highlightedIndex, isOpen])

    // Reset highlighted index when search changes
    useEffect(() => {
        setHighlightedIndex(0)
    }, [searchQuery])

    const handleCountrySelect = (country: Country) => {
        setSelectedCountry(country)
        setIsOpen(false)
        inputRef.current?.focus()

        if (localNumber) {
            onChange(`${country.dial}${localNumber}`)
        }
    }

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const cleaned = e.target.value.replace(/[^\d\s-]/g, "")
        setLocalNumber(cleaned)

        const digitsOnly = cleaned.replace(/\D/g, "")
        onChange(`${selectedCountry.dial}${digitsOnly}`)
    }

    const handleBlur = () => {
        onBlur?.()
    }

    const handleToggle = () => {
        if (disabled) return
        setIsOpen((prev) => !prev)
    }

    // Dropdown portal content
    const dropdownContent = isOpen && dropdownPosition && (
        <div
            ref={dropdownRef}
            className={cn(
                "fixed z-[100] flex max-h-80 flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg",
                "animate-in fade-in-0 zoom-in-95 duration-150"
            )}
            style={{
                top: dropdownPosition.top,
                left: dropdownPosition.left,
                width: dropdownPosition.width,
                transformOrigin: dropdownPosition.openAbove ? "bottom" : "top"
            }}
            role="listbox"
            aria-label="Select country"
        >
            {/* Search input - sticky at top */}
            <div className="sticky top-0 z-10 border-b border-gray-100 bg-white p-2">
                <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search countries..."
                    className={cn(
                        "h-10 w-full rounded-lg border-0 bg-gray-50 px-3 text-[14px]",
                        "placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-400/20"
                    )}
                    aria-label="Search countries"
                />
            </div>

            {/* Country list */}
            <div ref={listRef} className="flex-1 overflow-y-auto py-1">
                {filteredCountries.length === 0 ? (
                    <div className="px-3 py-6 text-center text-[14px] text-gray-500">
                        No countries found
                    </div>
                ) : (
                    filteredCountries.map((country, index) => (
                        <button
                            key={country.code}
                            type="button"
                            data-index={index}
                            onClick={() => handleCountrySelect(country)}
                            onMouseEnter={() => setHighlightedIndex(index)}
                            role="option"
                            aria-selected={selectedCountry.code === country.code}
                            className={cn(
                                "flex w-full items-center gap-3 px-3 py-2.5 text-left text-[14px] transition-colors",
                                highlightedIndex === index && "bg-gray-50",
                                selectedCountry.code === country.code && "bg-gray-100"
                            )}
                        >
                            <span className="text-lg">{country.flag}</span>
                            <span className="flex-1 text-gray-900">{country.name}</span>
                            <span className="text-gray-500">{country.dial}</span>
                        </button>
                    ))
                )}
            </div>
        </div>
    )

    return (
        <div className="flex flex-col gap-1.5">
            <div className="relative flex">
                {/* Country Selector */}
                <button
                    ref={triggerRef}
                    type="button"
                    onClick={handleToggle}
                    disabled={disabled}
                    aria-haspopup="listbox"
                    aria-expanded={isOpen}
                    className={cn(
                        "flex h-11 items-center gap-1.5 rounded-l-xl border border-r-0 border-gray-200 bg-gray-50 px-3",
                        "text-[14px] transition-all duration-200",
                        "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-gray-400",
                        disabled && "cursor-not-allowed opacity-50"
                    )}
                >
                    <span className="text-lg">{selectedCountry.flag}</span>
                    <span className="text-gray-600">{selectedCountry.dial}</span>
                    <svg
                        className={cn(
                            "h-4 w-4 text-gray-400 transition-transform duration-200",
                            isOpen && "rotate-180"
                        )}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>

                {/* Phone Number Input */}
                <input
                    ref={inputRef}
                    type="tel"
                    inputMode="tel"
                    value={localNumber}
                    onChange={handleNumberChange}
                    onBlur={handleBlur}
                    placeholder={placeholder}
                    disabled={disabled}
                    className={cn(
                        "h-11 flex-1 rounded-r-xl border border-gray-200 bg-white px-4",
                        "text-[14px] leading-normal text-gray-900 placeholder:text-gray-400",
                        "outline-none transition-all duration-200",
                        "focus:border-gray-400 focus:ring-2 focus:ring-gray-400/20",
                        disabled && "cursor-not-allowed bg-gray-50 text-gray-500",
                        error && "border-red-500 focus:border-red-500 focus:ring-red-500/20"
                    )}
                />
            </div>

            {/* Render dropdown in portal to escape overflow:hidden */}
            {typeof document !== "undefined" && createPortal(dropdownContent, document.body)}

            {/* Error message */}
            {error && (
                <p className="text-[13px] text-red-500" role="alert">
                    {error}
                </p>
            )}

        </div>
    )
}
